import { useRef, useState } from "react";
import { Paperclip, X } from "lucide-react";
import { api, ApiError, type ComposeAddress, type ComposeAttachment } from "../../lib/api-client";

// Precarga para Responder/Responder a todos/Reenviar (Fase 4) — "Redactar"
// desde cero pasa un draft vacío. inReplyTo/references solo van seteados
// al responder (ver MessageView.tsx buildReplyDraft).
export interface ComposeDraft {
  to?: string;
  cc?: string;
  subject?: string;
  body?: string;
  attachments?: ComposeAttachment[];
  inReplyTo?: string[];
  references?: string[];
}

interface ComposePaneProps {
  draft: ComposeDraft;
  onClose: () => void;
  onSent: () => void;
}

interface PendingAttachment {
  id: string;
  name: string;
  size: number;
  status: "uploading" | "done" | "error";
  uploaded?: ComposeAttachment;
}

function parseAddresses(raw: string): ComposeAddress[] {
  return raw
    .split(/[,;]/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((email) => ({ email }));
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ComposePane({ draft, onClose, onSent }: ComposePaneProps) {
  const [to, setTo] = useState(draft.to ?? "");
  const [cc, setCc] = useState(draft.cc ?? "");
  const [showCc, setShowCc] = useState(!!draft.cc);
  const [subject, setSubject] = useState(draft.subject ?? "");
  const [text, setText] = useState(draft.body ?? "");
  // Reenviar reusa los blobId ya subidos del correo original — sin
  // re-upload, ya están en Stalwart (ver MessageView.tsx handleForward).
  const [attachments, setAttachments] = useState<PendingAttachment[]>(
    () =>
      draft.attachments?.map((a) => ({
        id: a.blobId,
        name: a.name,
        size: a.size,
        status: "done" as const,
        uploaded: a,
      })) ?? [],
  );
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleFiles(files: FileList | null) {
    if (!files) return;
    for (const file of Array.from(files)) {
      const id = `${file.name}-${file.size}-${Date.now()}-${Math.random()}`;
      setAttachments((prev) => [...prev, { id, name: file.name, size: file.size, status: "uploading" }]);
      api
        .uploadAttachment(file)
        .then((uploaded) => {
          setAttachments((prev) =>
            prev.map((a) => (a.id === id ? { ...a, status: "done", uploaded } : a)),
          );
        })
        .catch(() => {
          setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, status: "error" } : a)));
        });
    }
  }

  function removeAttachment(id: string) {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const toAddresses = parseAddresses(to);
    if (toAddresses.length === 0) {
      setError("Agregá al menos un destinatario.");
      return;
    }
    if (!text.trim()) {
      setError("El mensaje no puede estar vacío.");
      return;
    }
    if (attachments.some((a) => a.status === "uploading")) {
      setError("Esperá a que terminen de subirse los adjuntos.");
      return;
    }

    setSending(true);
    setError(null);
    try {
      await api.sendMessage({
        to: toAddresses,
        cc: showCc ? parseAddresses(cc) : undefined,
        subject,
        text,
        attachments: attachments
          .filter((a): a is PendingAttachment & { uploaded: ComposeAttachment } => !!a.uploaded)
          .map((a) => a.uploaded),
        inReplyTo: draft.inReplyTo,
        references: draft.references,
      });
      onSent();
    } catch (err) {
      // El backend valida direcciones/tamaños con zod (400) y puede fallar
      // en el submit JMAP en sí (502) — cualquiera de los dos se muestra
      // igual acá, el detalle interno no le sirve al usuario.
      setError(
        err instanceof ApiError
          ? "No se pudo enviar el correo. Revisá los destinatarios e intentá de nuevo."
          : "No se pudo enviar el correo.",
      );
    } finally {
      setSending(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 24px",
          borderBottom: "0.5px solid var(--border)",
          flexShrink: 0,
        }}
      >
        <span style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)" }}>
          Nuevo mensaje
        </span>
        <button
          type="button"
          onClick={onClose}
          title="Cerrar"
          style={{
            display: "flex",
            border: "none",
            background: "transparent",
            color: "var(--text-muted)",
            cursor: "pointer",
            padding: 4,
          }}
        >
          <X size={18} />
        </button>
      </div>

      <div style={{ display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0, overflowY: "auto" }}>
        <FieldRow>
          <input
            type="text"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="Para (separá varios con coma)"
            autoFocus
            style={inputStyle}
          />
          {!showCc && (
            <button type="button" onClick={() => setShowCc(true)} style={ccToggleStyle}>
              Cc
            </button>
          )}
        </FieldRow>

        {showCc && (
          <FieldRow>
            <input
              type="text"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="Cc"
              style={inputStyle}
            />
          </FieldRow>
        )}

        <FieldRow>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Asunto"
            style={inputStyle}
          />
        </FieldRow>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Escribí tu mensaje…"
          style={{
            border: "none",
            outline: "none",
            resize: "none",
            padding: "16px 24px",
            fontSize: 14,
            lineHeight: 1.6,
            fontFamily: "var(--font-body)",
            color: "var(--text-primary)",
            background: "transparent",
            flex: "1 1 auto",
            minHeight: 200,
          }}
        />

        {attachments.length > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              padding: "0 24px 16px",
              flexShrink: 0,
            }}
          >
            {attachments.map((a) => (
              <div
                key={a.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 10px",
                  borderRadius: 8,
                  border: "0.5px solid var(--border)",
                  background: "var(--surface-2)",
                  fontSize: 12,
                }}
              >
                <span style={{ color: "var(--text-secondary)" }}>
                  {a.name} · {formatSize(a.size)}
                  {a.status === "uploading" && " · subiendo…"}
                  {a.status === "error" && " · error"}
                </span>
                <button
                  type="button"
                  onClick={() => removeAttachment(a.id)}
                  title="Quitar adjunto"
                  style={{
                    display: "flex",
                    border: "none",
                    background: "transparent",
                    color: "var(--text-muted)",
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {error && <p style={{ margin: "0 24px 8px", fontSize: 12, color: "#b91c1c" }}>{error}</p>}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 24px",
          borderTop: "0.5px solid var(--border)",
          flexShrink: 0,
        }}
      >
        <div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = "";
            }}
            style={{ display: "none" }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            title="Adjuntar archivo"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              border: "0.5px solid var(--border)",
              background: "var(--surface-1)",
              color: "var(--text-secondary)",
              borderRadius: 8,
              fontSize: 13,
              padding: "8px 14px",
              cursor: "pointer",
            }}
          >
            <Paperclip size={14} /> Adjuntar
          </button>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            style={{
              border: "none",
              background: "transparent",
              color: "var(--text-secondary)",
              cursor: sending ? "default" : "pointer",
              fontSize: 13,
              padding: "8px 12px",
            }}
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={sending}
            style={{
              border: "none",
              background: "var(--color-primary)",
              color: "#fff",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 500,
              padding: "8px 20px",
              cursor: sending ? "default" : "pointer",
              opacity: sending ? 0.7 : 1,
            }}
          >
            {sending ? "Enviando…" : "Enviar"}
          </button>
        </div>
      </div>
    </form>
  );
}

function FieldRow({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "12px 24px",
        borderBottom: "0.5px solid var(--border)",
        flexShrink: 0,
      }}
    >
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  flex: "1 1 auto",
  border: "none",
  outline: "none",
  fontSize: 14,
  fontFamily: "var(--font-body)",
  color: "var(--text-primary)",
  background: "transparent",
  minWidth: 0,
};

const ccToggleStyle: React.CSSProperties = {
  border: "none",
  background: "transparent",
  color: "var(--text-muted)",
  cursor: "pointer",
  fontSize: 12,
  flexShrink: 0,
};
